import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { PhotoPosition } from '../../types';

const SIZE_DIMS: Record<string, { width: number; height: number }> = {
  '2x4': { width: 2000, height: 4000 },
  '4x2': { width: 4000, height: 2000 },
  '2x6': { width: 2000, height: 6000 },
  '6x2': { width: 6000, height: 2000 },
  '4x6': { width: 4000, height: 6000 },
  '6x4': { width: 6000, height: 4000 },
  '3x4': { width: 3000, height: 4000 },
  '4x3': { width: 4000, height: 3000 },
};

const STACK_SIZE_KEYS = new Set(['2x4', '4x2', '3x4', '4x3', '2x6', '6x2']);

/**
 * Đơn vị "inch" px — lấy min(gốc 4x6, gốc 6x4)
 * → 2x6 và 6x2 cùng scale (chỉ xoay), không cái nào to hơn.
 */
function getInchUnit(boxW: number, baseMax: number) {
  const from46 = Math.min(boxW / 4, baseMax / 6);
  const from64 = Math.min(boxW / 6, baseMax / 4);
  return Math.min(from46, from64);
}

const SLOT_PINK = '#e86a8a';
const FRAME_BORDER = '#ff8ba7';

function getFrameSizeKey(size?: string) {
  return String(size || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/×/g, 'x');
}

function parseFrameAspect(size?: string) {
  const m = String(size || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!m) return { w: 4, h: 6 };
  return { w: Math.max(0.1, parseFloat(m[1])), h: Math.max(0.1, parseFloat(m[2])) };
}

function calculateFrameLayout(sizeKey: string, fw: number, fh: number) {
  const ratio = fw > 0 && fh > 0 ? fw / fh : 0;

  if (sizeKey === '4x2' || (ratio >= 1.9 && ratio <= 2.1)) {
    return { photosPerCell: 3, isLandscape: true };
  }
  if (sizeKey === '2x4' || (ratio >= 0.45 && ratio <= 0.55)) {
    return { photosPerCell: 3, isLandscape: false };
  }
  if (sizeKey === '6x2' || (ratio >= 2.9 && ratio <= 3.1)) {
    return { photosPerCell: 2, isLandscape: true };
  }
  if (sizeKey === '2x6' || (ratio >= 0.32 && ratio <= 0.34)) {
    return { photosPerCell: 2, isLandscape: false };
  }
  if (sizeKey === '3x4' || (ratio >= 0.75 && ratio <= 0.85)) {
    return { photosPerCell: 2, isLandscape: false };
  }
  if (sizeKey === '4x3' || (ratio >= 1.28 && ratio <= 1.38)) {
    return { photosPerCell: 2, isLandscape: true };
  }
  return { photosPerCell: 1, isLandscape: fw > fh };
}

function getStackStripOverlap(stripH: number, count: number, sizeKey: string, isMobile: boolean) {
  if (count >= 3) return stripH * 0.14;
  if (sizeKey === '4x3') return stripH * 0.5;
  if (sizeKey === '6x2') return stripH * (isMobile ? 0.38 : 0.05);
  return stripH * 0.12;
}

function getHorizontalFanGap(stripW: number, count: number, sizeKey: string) {
  if (count >= 3) return Math.max(stripW * 0.68, stripW * 0.52 + 6);
  if (sizeKey === '2x6') return Math.max(stripW * 0.72, stripW * 0.66 + 12);
  if (sizeKey === '3x4' || sizeKey === '4x3') return stripW * 0.3 + 5;
  return Math.max(stripW * 0.76, stripW * 0.72 + 10);
}

export interface CustomFrameLayoutPreviewProps {
  width?: number;
  height?: number;
  size?: string;
  photoPositions?: PhotoPosition[];
  /** Trần chiều cao preview (px) — 4x6/6x4 dùng gần hết */
  maxHeight?: number | { xs: number; sm: number };
  cellColor?: string;
  borderColor?: string;
}

interface CellsProps {
  fw: number;
  fh: number;
  positions: PhotoPosition[];
  cellColor: string;
  fill?: boolean;
  dispW?: number;
  dispH?: number;
  borderColor?: string;
  withBorder?: boolean;
}

const LayoutCells: React.FC<CellsProps> = ({
  fw,
  fh,
  positions,
  cellColor,
  fill,
  dispW,
  dispH,
  borderColor = FRAME_BORDER,
  withBorder,
}) => (
  <Box
    sx={{
      position: 'relative',
      width: fill ? '100%' : dispW,
      height: fill ? '100%' : dispH,
      bgcolor: '#fff',
      borderRadius: withBorder ? 1.5 : 0,
      border: withBorder ? '2px solid' : 'none',
      borderColor: withBorder ? cellColor : undefined,
      boxShadow: withBorder ? `0 2px 10px ${alpha(borderColor, 0.18)}` : undefined,
      overflow: 'hidden',
      flexShrink: 0,
      boxSizing: 'border-box',
    }}
  >
    {positions.length === 0 ? (
      <Box sx={{ width: '100%', height: '100%', bgcolor: alpha(cellColor, 0.12) }} />
    ) : (
      positions.map((pos, i) => {
        const left = ((Number(pos.x) || 0) / fw) * 100;
        const top = ((Number(pos.y) || 0) / fh) * 100;
        const w = Math.max(0.5, ((Number(pos.width) || 0) / fw) * 100);
        const h = Math.max(0.5, ((Number(pos.height) || 0) / fh) * 100);
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${left}%`,
              top: `${top}%`,
              width: `${w}%`,
              height: `${h}%`,
              bgcolor: cellColor,
              borderRadius: '1px',
              boxSizing: 'border-box',
            }}
          />
        );
      })
    )}
  </Box>
);

/**
 * Preview giống mobile-upload:
 * - 4x6 / 6x4 to nhất (1 khung)
 * - 2x6 / 6x2 / 3x4 / 4x3 → 2 strip
 * - 2x4 / 4x2 → 3 strip
 */
const CustomFrameLayoutPreview: React.FC<CustomFrameLayoutPreviewProps> = ({
  width,
  height,
  size,
  photoPositions = [],
  maxHeight = { xs: 200, sm: 240 },
  cellColor = SLOT_PINK,
  borderColor = FRAME_BORDER,
}) => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMobile = !isSmUp;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(0);

  const baseMax =
    typeof maxHeight === 'number' ? maxHeight : isSmUp ? maxHeight.sm : maxHeight.xs;

  const sizeKey = getFrameSizeKey(size);
  const { fw, fh } = useMemo(() => {
    const fromSize = SIZE_DIMS[sizeKey];
    return {
      fw: Math.max(1, Number(width) || fromSize?.width || 4000),
      fh: Math.max(1, Number(height) || fromSize?.height || 6000),
    };
  }, [width, height, sizeKey]);

  const layout = useMemo(
    () => calculateFrameLayout(sizeKey, fw, fh),
    [sizeKey, fw, fh],
  );
  const useStack = STACK_SIZE_KEYS.has(sizeKey) || layout.photosPerCell >= 2;

  // Ô chứa định cao — mọi thẻ bằng nhau; layout căn giữa bên trong
  const boxH = baseMax;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setBoxW(el.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseMax, sizeKey]);

  const stackLayout = useMemo(() => {
    if (!useStack || boxW <= 0 || boxH <= 0) return null;

    const count = layout.photosPerCell;
    const aspect = parseFrameAspect(size);
    const enlargedDual = sizeKey === '3x4' || sizeKey === '4x3' || sizeKey === '2x6';
    const isLandscape = layout.isLandscape;
    const isSixByTwo = sizeKey === '6x2';

    // Cùng unit min(4x6, 6x4) → 2x6 ↔ 6x2 cùng scale (chỉ xoay)
    const unit = getInchUnit(boxW, baseMax);
    let stripW = aspect.w * unit;
    let stripH = aspect.h * unit;

    // Fit vào ô (giữ tỉ lệ — scale đều)
    const pad = isSixByTwo ? 6 : 8;
    const fitMaxW = Math.max(24, boxW - pad * 2);
    const fitMaxH = Math.max(24, boxH - pad * 2);

    if (isLandscape) {
      const overlap = getStackStripOverlap(stripH, count, sizeKey, isMobile);
      const stackH = stripH + (count - 1) * (stripH - overlap);
      const rotPad = 1.12;
      const rotW = stripW * rotPad;
      const rotStackH = stackH * rotPad;
      const s = Math.min(1, fitMaxW / rotW, fitMaxH / rotStackH);
      stripW *= s;
      stripH *= s;
    } else {
      const inset = count >= 3 ? boxW * 0.02 : enlargedDual ? boxW * 0.03 : boxW * 0.05;
      const gap = getHorizontalFanGap(stripW, count, sizeKey);
      const fanW = inset * 2 + stripW + gap * (count - 1);
      const rotH = stripH * 1.08;
      const s = Math.min(1, fitMaxW / fanW, fitMaxH / rotH);
      stripW *= s;
      stripH *= s;
    }

    stripW = Math.round(stripW);
    stripH = Math.round(stripH);

    const strips: Array<{ left: number; top: number; rotate: number; zIndex: number }> = [];

    if (isLandscape) {
      const overlap = getStackStripOverlap(stripH, count, sizeKey, isMobile);
      const angles = count >= 3 ? [-3.5, 0, 3.5] : [-3, 3];
      const left = Math.max(4, Math.round((boxW - stripW) * 0.5));
      const stackH = stripH + (count - 1) * (stripH - overlap);
      const top = Math.max(8, Math.round((boxH - stackH) * 0.5));
      for (let i = 0; i < count; i++) {
        strips.push({
          left,
          top: Math.round(top + i * (stripH - overlap)),
          rotate: angles[i] ?? 0,
          zIndex: i + 1,
        });
      }
    } else {
      const inset = count >= 3 ? boxW * 0.02 : enlargedDual ? boxW * 0.03 : boxW * 0.05;
      const gap = getHorizontalFanGap(stripW, count, sizeKey);
      const fanW = inset * 2 + stripW + gap * (count - 1);
      const top = Math.max(4, Math.round((boxH - stripH) * 0.5));
      const angles = count >= 3 ? [-2.5, 0, 2.5] : [-2.5, 2.5];
      const startLeft = Math.max(2, Math.round((boxW - fanW) * 0.5 + inset));
      for (let i = 0; i < count; i++) {
        strips.push({
          left: Math.round(startLeft + i * gap),
          top,
          rotate: angles[i] ?? 0,
          zIndex: i,
        });
      }
    }

    return { stripW, stripH, strips, isLandscape };
  }, [useStack, boxW, boxH, baseMax, layout, size, sizeKey, isMobile]);

  // 4x6 / 6x4: cùng unit → cùng scale (chỉ xoay), không cái nào to hơn
  const singleDims = useMemo(() => {
    if (useStack || boxW <= 0) return { w: 0, h: 0 };
    const aspect = parseFrameAspect(sizeKey);
    const unit = getInchUnit(boxW, baseMax);
    let w = aspect.w * unit;
    let h = aspect.h * unit;
    const pad = 8;
    const s = Math.min(1, (boxW - pad) / w, (boxH - pad) / h);
    w = Math.max(1, Math.round(w * s));
    h = Math.max(1, Math.round(h * s));
    return { w, h };
  }, [useStack, boxW, boxH, baseMax, sizeKey]);

  return (
    <Box
      ref={wrapRef}
      sx={{
        width: '100%',
        height: boxH,
        minHeight: boxH,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Đủ chỗ cho strip xoay chéo — không cắt
        overflow: 'visible',
      }}
    >
      {useStack && stackLayout ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
        >
          {stackLayout.strips.map((s, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: s.left,
                top: s.top,
                width: stackLayout.stripW,
                height: stackLayout.stripH,
                bgcolor: '#fff',
                border: '2px solid',
                borderColor: cellColor,
                borderRadius: '6px',
                p: '4px',
                boxSizing: 'border-box',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                transform: `rotate(${s.rotate}deg)`,
                transformOrigin: stackLayout.isLandscape ? '50% 50%' : '50% 100%',
                zIndex: s.zIndex,
                pointerEvents: 'none',
              }}
            >
              <LayoutCells
                fw={fw}
                fh={fh}
                positions={photoPositions || []}
                cellColor={cellColor}
                fill
              />
            </Box>
          ))}
        </Box>
      ) : singleDims.w > 0 && singleDims.h > 0 ? (
        <LayoutCells
          fw={fw}
          fh={fh}
          positions={photoPositions || []}
          cellColor={cellColor}
          borderColor={borderColor}
          withBorder
          dispW={singleDims.w}
          dispH={singleDims.h}
        />
      ) : null}
    </Box>
  );
};

export default CustomFrameLayoutPreview;
